import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Assessment,
  Home
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface TrendReport {
  id: number;
  market_name: string;
  status: 'purchased' | 'submitted' | 'needs_adjustments' | 'completed' | 'cancelled';
  purchase_date: string;
  submitted_date: string | null;
  amount: number;
  currency: string;
  payment_method: string;
  user: {
    name: string;
    email: string;
  };
}

export const TrendReportsAdmin: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Check if MSAL is still initializing
  const isMSALBusy = inProgress !== InteractionStatus.None;
  
  // Check if we're authenticated but user data hasn't loaded yet
  const isWaitingForUserData = accounts.length > 0 && (isLoadingUser || !user);
  
  // Show loading spinner while authentication or user data is loading
  if (isMSALBusy || isWaitingForUserData) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Only redirect if we're sure the user has loaded and doesn't have permission
  // If there are no accounts, ProtectedRoute will handle the redirect
  if (accounts.length > 0 && user && !user.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }
  
  // If we have accounts but no user data, something went wrong
  if (accounts.length > 0 && !user) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            Failed to load user permissions. Please refresh the page or contact support.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Fetch Custom Comp Set Reports from API
  const { data: trendReportsData, isLoading, error } = useQuery({
    queryKey: ['trendReports'],
    queryFn: async (): Promise<TrendReport[]> => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/trend-reports`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch Custom Comp Set Reports');
      }
      const data = await response.json();
      return data.trendReports || [];
    },
    enabled: !!user
  });

  const trendReports = trendReportsData || [];

  // Separate reports into four categories
  const awaitingInputReports = trendReports.filter(report =>
    report.status === 'purchased' || report.status === 'needs_adjustments'
  );
  const pendingReports = trendReports.filter(report =>
    report.status !== 'completed' && report.status !== 'purchased' && report.status !== 'needs_adjustments' && report.status !== 'cancelled'
  );
  const completedReports = trendReports.filter(report => report.status === 'completed');
  const cancelledReports = trendReports.filter(report => report.status === 'cancelled');

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (amount: number, currency: string) => {
    if (!amount) return '-';
    return (amount / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'submitted':
        return 'error';
      case 'needs_adjustments':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'purchased':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'needs_adjustments':
        return 'needs adjustments';
      case 'cancelled':
        return 'cancelled';
      default:
        return status;
    }
  };

  const handleRowClick = (reportId: number) => {
    navigate(`/admin/hub/marketcompass/reports/${reportId}`);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Custom Comp Set Reports</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Assessment sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Custom Comp Set Reports
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage Custom Comp Set Reports and one-time purchase products
          </Typography>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load Custom Comp Set Reports. Please try again later.
          </Alert>
        )}

        {!isLoading && !error && (
          <>
            {/* Reports Ready for Implementation Table */}
            {pendingReports.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Reports Ready for Implementation
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 4, maxHeight: 600, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Market Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Price Paid</TableCell>
                        <TableCell>Payment Method</TableCell>
                        <TableCell>Purchase Date</TableCell>
                        <TableCell>Submitted Date</TableCell>
                        <TableCell>Customer Name</TableCell>
                        <TableCell>Customer Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingReports.map((report) => (
                        <TableRow
                          key={report.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(report.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {report.market_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(report.status)}
                              color={getStatusColor(report.status)}
                              size="small"
                              sx={report.status === 'purchased' ? {
                                backgroundColor: 'white',
                                color: 'rgba(0, 0, 0, 0.87)',
                                border: '1px solid rgba(0, 0, 0, 0.23)'
                              } : {}}
                            />
                          </TableCell>
                          <TableCell>{formatPrice(report.amount, report.currency)}</TableCell>
                          <TableCell>{report.payment_method || '-'}</TableCell>
                          <TableCell>{formatDate(report.purchase_date)}</TableCell>
                          <TableCell>{formatDate(report.submitted_date)}</TableCell>
                          <TableCell>{report.user.name || '-'}</TableCell>
                          <TableCell>{report.user.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Reports Awaiting User Input Table */}
            {awaitingInputReports.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Reports Awaiting User Input
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 4, maxHeight: 600, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Market Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Price Paid</TableCell>
                        <TableCell>Payment Method</TableCell>
                        <TableCell>Purchase Date</TableCell>
                        <TableCell>Submitted Date</TableCell>
                        <TableCell>Customer Name</TableCell>
                        <TableCell>Customer Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {awaitingInputReports.map((report) => (
                        <TableRow
                          key={report.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(report.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {report.market_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(report.status)}
                              color={getStatusColor(report.status)}
                              size="small"
                              sx={report.status === 'purchased' ? {
                                backgroundColor: 'white',
                                color: 'rgba(0, 0, 0, 0.87)',
                                border: '1px solid rgba(0, 0, 0, 0.23)'
                              } : {}}
                            />
                          </TableCell>
                          <TableCell>{formatPrice(report.amount, report.currency)}</TableCell>
                          <TableCell>{report.payment_method || '-'}</TableCell>
                          <TableCell>{formatDate(report.purchase_date)}</TableCell>
                          <TableCell>{formatDate(report.submitted_date)}</TableCell>
                          <TableCell>{report.user.name || '-'}</TableCell>
                          <TableCell>{report.user.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Completed Reports Table */}
            {completedReports.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Completed Reports
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 4, maxHeight: 600, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Market Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Price Paid</TableCell>
                        <TableCell>Payment Method</TableCell>
                        <TableCell>Purchase Date</TableCell>
                        <TableCell>Submitted Date</TableCell>
                        <TableCell>Customer Name</TableCell>
                        <TableCell>Customer Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {completedReports.map((report) => (
                        <TableRow
                          key={report.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(report.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {report.market_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(report.status)}
                              color={getStatusColor(report.status)}
                              size="small"
                              sx={report.status === 'purchased' ? {
                                backgroundColor: 'white',
                                color: 'rgba(0, 0, 0, 0.87)',
                                border: '1px solid rgba(0, 0, 0, 0.23)'
                              } : {}}
                            />
                          </TableCell>
                          <TableCell>{formatPrice(report.amount, report.currency)}</TableCell>
                          <TableCell>{report.payment_method || '-'}</TableCell>
                          <TableCell>{formatDate(report.purchase_date)}</TableCell>
                          <TableCell>{formatDate(report.submitted_date)}</TableCell>
                          <TableCell>{report.user.name || '-'}</TableCell>
                          <TableCell>{report.user.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Cancelled Reports Table */}
            {cancelledReports.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Cancelled Reports
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 4, maxHeight: 600, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Market Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Price Paid</TableCell>
                        <TableCell>Payment Method</TableCell>
                        <TableCell>Purchase Date</TableCell>
                        <TableCell>Submitted Date</TableCell>
                        <TableCell>Customer Name</TableCell>
                        <TableCell>Customer Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cancelledReports.map((report) => (
                        <TableRow
                          key={report.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(report.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {report.market_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(report.status)}
                              color={getStatusColor(report.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatPrice(report.amount, report.currency)}</TableCell>
                          <TableCell>{report.payment_method || '-'}</TableCell>
                          <TableCell>{formatDate(report.purchase_date)}</TableCell>
                          <TableCell>{formatDate(report.submitted_date)}</TableCell>
                          <TableCell>{report.user.name || '-'}</TableCell>
                          <TableCell>{report.user.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}

        {!isLoading && !error && trendReports.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Assessment sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Custom Comp Set Reports Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Custom Comp Set Reports will appear here when customers purchase them from the store.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};